/**
 * StorytellingPage - two-mode visual editor
 *
 * Mode "scene":  [Scene list] | [Action timeline] | [Action detail panel]
 * Mode "story":  [Story list] | [SVG DAG canvas]
 *
 * @author    Claude / contexagon GmbH
 */

import React from 'react';
import { connect } from 'react-redux';
import {
    REST_CALL_OBJECT_LIST,
    REST_CALL_LOAD_OBJECT,
    REST_CALL_SAVE_OBJECT,
    REST_SERVER_BASE_URL,
} from '../../config';
import { editObject } from '../../operations/editing';
import SceneTimelineEditor from './SceneTimelineEditor';
import ActionDetailPanel from './ActionDetailPanel';
import ActionToolbox from './ActionToolbox';
import StoryFlowCanvas from './StoryFlowCanvas';


// ── Reference ID extraction ───────────────────────────────────────────────────
// Scans a flat list of actions and collects all referenced object IDs that
// need to be resolved via API to show meaningful names in the UI.

function collectReferenceIds(actions) {
    const ids = new Set();
    for (const action of actions) {
        const f = action.fields || {};
        const addIds = arr => Array.isArray(arr) && arr.forEach(id => typeof id === 'string' && id && ids.add(id));
        switch (action.type) {
            case 'contentaction':  addIds(f.content);   break;
            case 'stateaction':    addIds(f.property);  break;
            case 'sceneaction':    addIds(f.scene);     break;
            case 'mediaaction':    addIds(f.medialist); break;
            case 'menuaction':     addIds(f.menu);      break;
            default: break;
        }
    }
    return [...ids];
}

// ── Object ID generation ──────────────────────────────────────────────────────
// IDs in Pulsar follow the convention: "type.XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"

function generateObjectId(typeName) {
    const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1).toUpperCase();
    const uuid = `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
    return `${typeName}.${uuid}`;
}

// ── Story membership lookup ───────────────────────────────────────────────────
// Given a list of stories (each with element fields entry/exit/refusal),
// returns the subset that directly reference the given sceneId.

function findStoriesForScene(stories, sceneId) {
    if (!sceneId || !stories || stories.length === 0) return [];
    return stories.filter(story => {
        const f = story.fields || {};
        const sels = [
            ...(Array.isArray(f.entry)   ? f.entry   : []),
            ...(Array.isArray(f.exit)    ? f.exit    : []),
            ...(Array.isArray(f.refusal) ? f.refusal : []),
        ];
        return sels.some(sel => {
            const ids = sel && sel.fields && sel.fields.scene;
            return Array.isArray(ids) ? ids.includes(sceneId) : ids === sceneId;
        });
    });
}


// ── Mode toggle button ────────────────────────────────────────────────────────

function ModeTab({ label, active, onClick }) {
    return (
        <button
            onClick={onClick}
            style={{
                padding: '6px 18px',
                border: 'none',
                borderBottom: active ? '2px solid #1976d2' : '2px solid transparent',
                backgroundColor: 'transparent',
                cursor: active ? 'default' : 'pointer',
                fontSize: 13,
                fontWeight: active ? 700 : 400,
                color: active ? '#1976d2' : '#888',
                outline: 'none',
                transition: 'color 0.1s',
            }}
        >
            {label}
        </button>
    );
}


class StorytellingPage extends React.Component {

    state = {
        // shared
        mode: 'scene',   // 'scene' | 'story'

        // scene mode
        scenes: [],
        selectedSceneId: null,
        selectedScene: null,
        actions: [],
        selectedActionIndex: null,
        dirty: false,
        sceneSearchText: '',
        loadingScenes: false,
        loadingScene: false,
        saving: false,
        saveError: null,

        // reference object cache: { [id]: loadedObject }
        // populated by loadActionReferences after loading a scene
        referencedObjects: {},

        // new scene inline form
        showNewSceneInput: false,
        newSceneName: '',
        creatingScene: false,

        // story mode
        stories: [],
        selectedStoryId: null,
        selectedStory: null,
        storyScenes: {},   // cache: { [sceneId]: sceneObject }
        storySearchText: '',
        loadingStories: false,
        loadingStory: false,
    };

    componentDidMount() {
        this.loadScenes();
        this.loadStories();
    }

    // ── Scene mode ────────────────────────────────────────────────────────────

    async loadScenes() {
        this.setState({ loadingScenes: true });
        try {
            const scenes = await this.props.fetchScenes();
            const sorted = (scenes || []).slice().sort((a, b) => {
                const nameA = (a.fields && a.fields.name) || a.title || '';
                const nameB = (b.fields && b.fields.name) || b.title || '';
                return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
            });
            this.setState({ scenes: sorted });
        } catch (err) {
            console.error('StorytellingPage: failed to load scenes', err);
        } finally {
            this.setState({ loadingScenes: false });
        }
    }

    async selectScene(scene) {
        if (this.state.dirty) {
            if (!window.confirm('Ungespeicherte Änderungen verwerfen?')) return;
        }
        this.setState({
            selectedSceneId: scene.id,
            selectedScene: null,
            actions: [],
            selectedActionIndex: null,
            dirty: false,
            loadingScene: true,
            saveError: null,
        });
        try {
            const full = await this.props.loadObject(scene.id);
            const actions = (full && full.fields && full.fields.actions) || [];
            // Also load scene-level device references (target = cbox/channel IDs)
            const sceneTargetIds = full && full.fields && Array.isArray(full.fields.target)
                ? full.fields.target.filter(id => typeof id === 'string')
                : [];
            this.setState({ selectedScene: full, actions });
            // Load referenced objects in the background
            this.loadActionReferences(actions, sceneTargetIds);
        } catch (err) {
            console.error('StorytellingPage: failed to load scene', err);
        } finally {
            this.setState({ loadingScene: false });
        }
    }

    async loadActionReferences(actions, extraIds = []) {
        const idSet = new Set([
            ...collectReferenceIds(actions),
            ...extraIds.filter(id => typeof id === 'string' && id),
        ]);
        const ids = [...idSet];
        if (ids.length === 0) return;
        // Only fetch IDs not yet in cache
        const toLoad = ids.filter(id => !this.state.referencedObjects[id]);
        if (toLoad.length === 0) return;
        try {
            const results = await Promise.all(
                toLoad.map(id => this.props.loadObject(id).catch(() => null))
            );
            const newRefs = {};
            results.forEach((obj, i) => { if (obj) newRefs[toLoad[i]] = obj; });
            this.setState(prev => ({
                referencedObjects: { ...prev.referencedObjects, ...newRefs },
            }));
        } catch (err) {
            console.error('StorytellingPage: failed to load action references', err);
        }
    }

    async createScene(name) {
        if (!name || !name.trim()) return;
        this.setState({ creatingScene: true });
        try {
            const id = generateObjectId('scene');
            const newScene = {
                id,
                type: 'scene',
                title: name.trim(),
                fields: { name: name.trim(), actions: [] },
            };
            await this.props.saveScene(newScene);
            // Reload scene list then select the new scene
            await this.loadScenes();
            this.selectScene({ id });
        } catch (err) {
            console.error('StorytellingPage: createScene failed', err);
            alert('Fehler beim Erstellen der Szene.');
        } finally {
            this.setState({ creatingScene: false, showNewSceneInput: false, newSceneName: '' });
        }
    }

    handleReorder(fromIndex, toIndex) {
        const actions = [...this.state.actions];
        actions.splice(toIndex, 0, actions.splice(fromIndex, 1)[0]);
        let sel = this.state.selectedActionIndex;
        if (sel !== null) {
            if (sel === fromIndex) sel = toIndex;
            else if (fromIndex < toIndex && sel > fromIndex && sel <= toIndex) sel--;
            else if (fromIndex > toIndex && sel >= toIndex && sel < fromIndex) sel++;
        }
        this.setState({ actions, dirty: true, selectedActionIndex: sel });
    }

    handleDeleteAction(index) {
        if (!window.confirm('Aktion wirklich löschen?')) return;
        const actions = [...this.state.actions];
        actions.splice(index, 1);
        let sel = this.state.selectedActionIndex;
        if (sel === index) sel = null;
        else if (sel !== null && sel > index) sel--;
        this.setState({ actions, dirty: true, selectedActionIndex: sel });
    }

    handleSelectAction(index) {
        this.setState(prev => ({
            selectedActionIndex: prev.selectedActionIndex === index ? null : index,
        }));
    }

    handleUpdateActionFields(index, newFields) {
        const actions = [...this.state.actions];
        actions[index] = { ...actions[index], fields: newFields };
        this.setState({ actions, dirty: true });
    }

    async handleSave() {
        const { selectedScene, actions } = this.state;
        if (!selectedScene) return;
        this.setState({ saving: true, saveError: null });
        try {
            const updated = { ...selectedScene, fields: { ...selectedScene.fields, actions } };
            await this.props.saveScene(updated);
            this.setState({ selectedScene: updated, dirty: false });
        } catch (err) {
            console.error('StorytellingPage: save failed', err);
            this.setState({ saveError: 'Speichern fehlgeschlagen.' });
        } finally {
            this.setState({ saving: false });
        }
    }

    handleOpenEditor() {
        const { selectedSceneId } = this.state;
        if (selectedSceneId) {
            this.props.openInEditor(selectedSceneId);
        }
    }

    // ── Story mode ────────────────────────────────────────────────────────────

    async loadStories() {
        this.setState({ loadingStories: true });
        try {
            const stories = await this.props.fetchStories();
            const sorted = (stories || []).slice().sort((a, b) => {
                const nameA = (a.fields && a.fields.name) || a.title || '';
                const nameB = (b.fields && b.fields.name) || b.title || '';
                return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
            });
            this.setState({ stories: sorted });
        } catch (err) {
            console.error('StorytellingPage: failed to load stories', err);
        } finally {
            this.setState({ loadingStories: false });
        }
    }

    async selectStory(story) {
        this.setState({
            selectedStoryId: story.id,
            selectedStory: null,
            storyScenes: {},
            loadingStory: true,
        });
        try {
            const full = await this.props.loadObject(story.id);
            // Collect all referenced scene IDs from entry/exit/refusal
            const allSceneIds = new Set();
            const fields = (full && full.fields) || {};
            ['entry', 'exit', 'refusal'].forEach(key => {
                (fields[key] || []).forEach(sel => {
                    ((sel.fields && sel.fields.scene) || []).forEach(sid => allSceneIds.add(sid));
                });
            });

            // Load all referenced scenes in parallel, then BFS for scene-action targets
            const loadedScenes = {};
            await this.loadScenesRecursive([...allSceneIds], loadedScenes);

            this.setState({ selectedStory: full, storyScenes: loadedScenes });
        } catch (err) {
            console.error('StorytellingPage: failed to load story', err);
        } finally {
            this.setState({ loadingStory: false });
        }
    }

    async loadScenesRecursive(sceneIds, cache, depth = 0) {
        if (depth > 20) return; // safety limit
        const toLoad = sceneIds.filter(id => !cache[id]);
        if (toLoad.length === 0) return;

        const results = await Promise.all(
            toLoad.map(id => this.props.loadObject(id).catch(() => null))
        );

        const nextIds = [];
        results.forEach((scene, i) => {
            if (!scene) return;
            cache[toLoad[i]] = scene;
            // Collect sceneaction targets for next BFS level
            if (scene.fields && Array.isArray(scene.fields.actions)) {
                scene.fields.actions.forEach(action => {
                    if (action.type === 'sceneaction') {
                        ((action.fields && action.fields.scene) || []).forEach(tid => {
                            if (!cache[tid]) nextIds.push(tid);
                        });
                    }
                });
            }
        });

        await this.loadScenesRecursive(nextIds, cache, depth + 1);
    }

    handleSelectSceneFromGraph(sceneId) {
        this.setState({ mode: 'scene' });
        // Find the scene in our scenes list to call selectScene
        const scene = this.state.scenes.find(s => s.id === sceneId);
        if (scene) {
            this.selectScene(scene);
        } else {
            // Scene not in list yet — load it directly
            this.selectScene({ id: sceneId });
        }
    }

    // ── Render ────────────────────────────────────────────────────────────────

    render() {
        const { mode } = this.state;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 110px)', overflow: 'hidden', backgroundColor: '#f5f5f5' }}>

                {/* ── Mode toggle ─────────────────────────────────────────── */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e0e0e0',
                    padding: '0 16px',
                    flexShrink: 0,
                    gap: 4,
                }}>
                    <ModeTab
                        label="Szenen-Ansicht"
                        active={mode === 'scene'}
                        onClick={() => this.setState({ mode: 'scene' })}
                    />
                    <ModeTab
                        label="Story-Graph"
                        active={mode === 'story'}
                        onClick={() => this.setState({ mode: 'story' })}
                    />
                </div>

                {/* ── Content area ─────────────────────────────────────────── */}
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                    {mode === 'scene' && this.renderSceneMode()}
                    {mode === 'story' && this.renderStoryMode()}

                </div>
            </div>
        );
    }

    renderSceneMode() {
        const {
            scenes, selectedSceneId, selectedScene, actions,
            selectedActionIndex, dirty, sceneSearchText,
            loadingScenes, loadingScene, saving, saveError,
            referencedObjects, stories,
            showNewSceneInput, newSceneName, creatingScene,
        } = this.state;

        const filtered = scenes.filter(s => {
            const name = (s.fields && s.fields.name) || s.title || '';
            return name.toLowerCase().includes(sceneSearchText.toLowerCase());
        });

        const selectedAction = selectedActionIndex !== null ? actions[selectedActionIndex] : null;
        const showDetailPanel = selectedScene && !loadingScene;

        // Which stories directly reference the selected scene (via entry/exit/refusal)?
        const storyMembership = findStoriesForScene(stories, selectedSceneId);

        return (
            <>
                {/* ── Col 1: Scene list ─────────────────────────────────── */}
                <div style={{
                    width: 240,
                    flexShrink: 0,
                    borderRight: '1px solid #e0e0e0',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'white',
                }}>
                    <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                            <div style={{ flex: 1, fontSize: 10, fontWeight: 800, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1.2 }}>
                                Szenen
                            </div>
                            <button
                                onClick={() => this.setState({ showNewSceneInput: true, newSceneName: '' })}
                                title="Neue Szene anlegen"
                                style={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 4,
                                    backgroundColor: 'white',
                                    color: '#1976d2',
                                    cursor: 'pointer',
                                    fontSize: 16,
                                    lineHeight: 1,
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 0,
                                    flexShrink: 0,
                                }}
                            >+</button>
                        </div>

                        {/* Inline new scene form */}
                        {showNewSceneInput && (
                            <form
                                onSubmit={e => { e.preventDefault(); this.createScene(newSceneName); }}
                                style={{ marginBottom: 8, display: 'flex', gap: 4 }}
                            >
                                <input
                                    autoFocus
                                    type="text"
                                    placeholder="Szenenname…"
                                    value={newSceneName}
                                    onChange={e => this.setState({ newSceneName: e.target.value })}
                                    disabled={creatingScene}
                                    style={{
                                        flex: 1,
                                        padding: '5px 8px',
                                        border: '1px solid #90caf9',
                                        borderRadius: 4,
                                        fontSize: 13,
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                    }}
                                />
                                <button type="submit" disabled={!newSceneName.trim() || creatingScene} style={{ border: 'none', borderRadius: 4, backgroundColor: '#1976d2', color: 'white', cursor: 'pointer', padding: '0 8px', fontSize: 14 }}>✓</button>
                                <button type="button" onClick={() => this.setState({ showNewSceneInput: false })} style={{ border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: 'white', color: '#999', cursor: 'pointer', padding: '0 8px', fontSize: 14 }}>✕</button>
                            </form>
                        )}

                        <input
                            type="text"
                            placeholder="Suchen…"
                            value={sceneSearchText}
                            onChange={e => this.setState({ sceneSearchText: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '6px 10px',
                                border: '1px solid #e8e8e8',
                                borderRadius: 6,
                                fontSize: 13,
                                boxSizing: 'border-box',
                                outline: 'none',
                                backgroundColor: '#fafafa',
                            }}
                        />
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {loadingScenes && (
                            <div style={{ padding: 12, color: '#bbb', fontSize: 13 }}>Lädt…</div>
                        )}
                        {!loadingScenes && filtered.length === 0 && (
                            <div style={{ padding: 12, color: '#bbb', fontSize: 13 }}>Keine Szenen gefunden.</div>
                        )}
                        {filtered.map(scene => {
                            const name = (scene.fields && scene.fields.name) || scene.title || scene.id;
                            const isSelected = scene.id === selectedSceneId;
                            const actionCount = scene.fields && Array.isArray(scene.fields.actions)
                                ? scene.fields.actions.length : undefined;
                            const category = scene.fields && scene.fields.category;
                            // Which stories reference this scene directly?
                            const sceneStories = findStoriesForScene(stories, scene.id);
                            return (
                                <div
                                    key={scene.id}
                                    onClick={() => this.selectScene(scene)}
                                    style={{
                                        padding: '9px 14px',
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? '#e8f4fd' : 'transparent',
                                        borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent',
                                        fontSize: 13,
                                        color: isSelected ? '#1565c0' : '#333',
                                    }}
                                >
                                    {category && (
                                        <div style={{ fontSize: 9, fontWeight: 800, color: isSelected ? '#7986cb' : '#bbb', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>
                                            {category}
                                        </div>
                                    )}
                                    <div style={{ fontWeight: isSelected ? 600 : 400, lineHeight: 1.3 }}>{name}</div>
                                    {actionCount !== undefined && (
                                        <div style={{ fontSize: 11, color: isSelected ? '#90caf9' : '#bbb', marginTop: 1 }}>
                                            {actionCount} Aktion{actionCount !== 1 ? 'en' : ''}
                                        </div>
                                    )}
                                    {sceneStories.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                                            {sceneStories.map(story => (
                                                <span key={story.id} style={{
                                                    fontSize: 9,
                                                    padding: '1px 5px',
                                                    borderRadius: 6,
                                                    backgroundColor: isSelected ? '#bbdefb' : '#f3e5f5',
                                                    color: isSelected ? '#1565c0' : '#7b1fa2',
                                                    fontWeight: 600,
                                                }}>
                                                    {(story.fields && story.fields.name) || story.title || '?'}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <ActionToolbox />
                </div>

                {/* ── Col 2: Timeline ───────────────────────────────────── */}
                <div style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: 20,
                }}>
                    {!selectedScene && !loadingScene && (
                        <div style={{ color: '#ccc', textAlign: 'center', marginTop: 80, fontSize: 15 }}>
                            Szene auswählen, um den Ablaufplan zu sehen
                        </div>
                    )}
                    {loadingScene && (
                        <div style={{ color: '#bbb', textAlign: 'center', marginTop: 80, fontSize: 15 }}>
                            Lädt Szene…
                        </div>
                    )}
                    {saveError && (
                        <div style={{
                            marginBottom: 12,
                            padding: '8px 14px',
                            backgroundColor: '#ffebee',
                            border: '1px solid #ef9a9a',
                            borderRadius: 6,
                            color: '#c62828',
                            fontSize: 13,
                            flexShrink: 0,
                        }}>
                            {saveError}
                        </div>
                    )}
                    {selectedScene && !loadingScene && (
                        <SceneTimelineEditor
                            scene={selectedScene}
                            actions={actions}
                            dirty={dirty}
                            saving={saving}
                            selectedActionIndex={selectedActionIndex}
                            referencedObjects={referencedObjects}
                            storyMembership={storyMembership}
                            onReorder={(from, to) => this.handleReorder(from, to)}
                            onSave={() => this.handleSave()}
                            onOpenEditor={() => this.handleOpenEditor()}
                            onSelectAction={index => this.handleSelectAction(index)}
                            onDeleteAction={index => this.handleDeleteAction(index)}
                        />
                    )}
                </div>

                {/* ── Col 3: Action detail panel ────────────────────────── */}
                {showDetailPanel && (
                    <div style={{
                        width: 290,
                        flexShrink: 0,
                        borderLeft: '1px solid #e0e0e0',
                        backgroundColor: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        <ActionDetailPanel
                            action={selectedAction}
                            index={selectedActionIndex}
                            referencedObjects={referencedObjects}
                            onUpdate={(index, newFields) => this.handleUpdateActionFields(index, newFields)}
                            onClose={() => this.setState({ selectedActionIndex: null })}
                        />
                    </div>
                )}
            </>
        );
    }

    renderStoryMode() {
        const {
            stories, selectedStoryId, selectedStory, storyScenes,
            storySearchText, loadingStories, loadingStory,
        } = this.state;

        const filtered = stories.filter(s => {
            const name = (s.fields && s.fields.name) || s.title || '';
            return name.toLowerCase().includes(storySearchText.toLowerCase());
        });

        return (
            <>
                {/* ── Col 1: Story list ─────────────────────────────────── */}
                <div style={{
                    width: 240,
                    flexShrink: 0,
                    borderRight: '1px solid #e0e0e0',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'white',
                }}>
                    <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 8 }}>
                            Stories
                        </div>
                        <input
                            type="text"
                            placeholder="Suchen…"
                            value={storySearchText}
                            onChange={e => this.setState({ storySearchText: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '6px 10px',
                                border: '1px solid #e8e8e8',
                                borderRadius: 6,
                                fontSize: 13,
                                boxSizing: 'border-box',
                                outline: 'none',
                                backgroundColor: '#fafafa',
                            }}
                        />
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {loadingStories && (
                            <div style={{ padding: 12, color: '#bbb', fontSize: 13 }}>Lädt…</div>
                        )}
                        {!loadingStories && filtered.length === 0 && (
                            <div style={{ padding: 12, color: '#bbb', fontSize: 13 }}>Keine Stories gefunden.</div>
                        )}
                        {filtered.map(story => {
                            const name = (story.fields && story.fields.name) || story.title || story.id;
                            const isSelected = story.id === selectedStoryId;
                            return (
                                <div
                                    key={story.id}
                                    onClick={() => this.selectStory(story)}
                                    style={{
                                        padding: '9px 14px',
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? '#e8f4fd' : 'transparent',
                                        borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent',
                                        fontSize: 13,
                                        color: isSelected ? '#1565c0' : '#333',
                                        fontWeight: isSelected ? 600 : 400,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {name}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ── Col 2: DAG Canvas ─────────────────────────────────── */}
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <StoryFlowCanvas
                        story={selectedStory}
                        scenes={storyScenes}
                        selectedSceneId={null}
                        loading={loadingStory}
                        onSelectScene={sceneId => this.handleSelectSceneFromGraph(sceneId)}
                    />
                </div>
            </>
        );
    }
}


const mapDispatchToProps = dispatch => ({
    fetchScenes: () => dispatch((dispatch, getState) => {
        const { authorization } = getState();
        return fetch(REST_SERVER_BASE_URL + REST_CALL_OBJECT_LIST.path, {
            method: REST_CALL_OBJECT_LIST.method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + authorization.accessToken,
            },
            body: JSON.stringify({ type: 'scene', 'timestamp-deleted': { $exists: false } }),
        }).then(r => r.json()).then(d => d.items || []);
    }),

    fetchStories: () => dispatch((dispatch, getState) => {
        const { authorization } = getState();
        return fetch(REST_SERVER_BASE_URL + REST_CALL_OBJECT_LIST.path, {
            method: REST_CALL_OBJECT_LIST.method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + authorization.accessToken,
            },
            body: JSON.stringify({ type: 'story', 'timestamp-deleted': { $exists: false } }),
        }).then(r => r.json()).then(d => d.items || []);
    }),

    loadObject: id => dispatch((dispatch, getState) => {
        const { authorization } = getState();
        return fetch(
            REST_SERVER_BASE_URL + REST_CALL_LOAD_OBJECT.path.replace('<id>', id),
            {
                method: REST_CALL_LOAD_OBJECT.method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + authorization.accessToken,
                },
            }
        ).then(r => r.json()).then(d => d.data);
    }),

    saveScene: scene => dispatch((dispatch, getState) => {
        const { authorization } = getState();
        return fetch(REST_SERVER_BASE_URL + REST_CALL_SAVE_OBJECT.path, {
            method: REST_CALL_SAVE_OBJECT.method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + authorization.accessToken,
            },
            body: JSON.stringify(scene),
        }).then(r => {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
        });
    }),

    openInEditor: objectId => dispatch(editObject(objectId)),
});

export default connect(null, mapDispatchToProps)(StorytellingPage);
